// TopToolbarCustomActions.tsx
import type { IPublicClientApplication } from "@azure/msal-browser";
import { useAccount, useMsal } from "@azure/msal-react";
import { Anchor, Drawer, ScrollArea, Tabs, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import type { InfiniteData } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useLiveQuery } from "dexie-react-hooks";
import type { MRT_ColumnFiltersState, MRT_TableInstance } from "mantine-react-table";
import { memo, useEffect, useMemo, useState } from "react";
import { ChatRouteChild } from "src/containers/ChatRoute";
import { useTab } from "src/contexts/tabContext";
import { ChatEntity, db } from "src/db";
import { CHAT_COMPLETIONS_URL } from "src/hooks/useChatCompletion";
import { useColumns } from "src/hooks/useDatasources";
import { useArtifactHandler } from "../utils/artifactHandler";
import { NativePdfViewer } from "./PDFViewer/NativePdfViewer";
export const AsyncPdfViewer = memo(
    ({
        pdfUrl,
        apiKey: _apiKey,
        row,
        instance: _instance,
    }: {
        pdfUrl: string;
        apiKey?: string;
        row: string;
        instance: IPublicClientApplication;
    }) => {
        const [pdfUrlWithApiKey, setPdfUrlWithApiKey] = useState<string | null>(null);
        const { getPdfUrl } = useArtifactHandler();

        useEffect(() => {
            (async () => {
                const url = await getPdfUrl(pdfUrl);
                setPdfUrlWithApiKey(url);
            })();
        }, [pdfUrl, getPdfUrl]);

        if (pdfUrlWithApiKey) {
            return <NativePdfViewer file={{ id: row, url: pdfUrlWithApiKey }} />;
        }
    },
);

interface TableData {
    _id: string;
    _source: {
        metadata: Record<string, string>;
    };
}

const PDFList = memo(
    ({
        rows,
        apiKey,
        instance,
        metadataDocumentsLive,
    }: {
        rows: string[];
        apiKey?: string;
        instance: IPublicClientApplication;
        // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
        metadataDocumentsLive: any[];
    }) => {
        const [activeTab, setActiveTab] = useState(rows[0]);

        return (
            <Tabs
                value={activeTab}
                onChange={(value) => setActiveTab(value || rows[0])}
                orientation="vertical"
                variant="pills"
                color="purple"
                styles={{
                    root: { height: "100%" },
                    panel: { height: "100%", paddingLeft: 0 },
                    list: {
                        borderRight: "1px solid #e5e7eb",
                        backgroundColor: "#f9fafb",
                        width: "220px",
                        padding: "12px 8px",
                        gap: "4px",
                    },
                    tab: {
                        padding: "12px 16px",
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#374151",
                        transition: "all 0.2s",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        "&:hover": {
                            backgroundColor: "#e5e7eb",
                        },
                        "&[data-active]": {
                            backgroundColor: "#8A00E5",
                            color: "white",
                            fontWeight: 600,
                        },
                    },
                    tabLabel: {
                        fontWeight: "inherit",
                        color: "inherit !important",
                    },
                    tabSection: {
                        color: "inherit !important",
                        "& *": {
                            color: "inherit !important",
                        },
                    },
                }}
            >
                <Tabs.List>
                    {rows.map((row, index) => {
                        const displayName = row.split("_")[0];
                        const shortName =
                            displayName.length > 20
                                ? `${displayName.slice(0, 8)}...${displayName.slice(-9)}`
                                : displayName;
                        const isActive = activeTab === row;
                        return (
                            <Tabs.Tab
                                key={row}
                                value={row}
                                title={displayName}
                                leftSection={
                                    <span
                                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/20 text-xs font-semibold"
                                        style={{ color: isActive ? "white" : "inherit" }}
                                    >
                                        {index + 1}
                                    </span>
                                }
                                style={{ color: isActive ? "white" : "#374151" }}
                            >
                                <span style={{ color: isActive ? "white" : "inherit" }}>
                                    {shortName}
                                </span>
                            </Tabs.Tab>
                        );
                    })}
                </Tabs.List>
                {rows.map((row) => {
                    const source = metadataDocumentsLive.findLast((doc) =>
                        doc._id.includes(row.split("_")[0]),
                    )?.fields.source;
                    const pdfUrl = `${CHAT_COMPLETIONS_URL}${source?.startsWith("/") ? "" : "/"}${source}`;
                    return (
                        <Tabs.Panel key={row} value={row}>
                            <div className="h-full w-full">
                                <AsyncPdfViewer
                                    instance={instance}
                                    pdfUrl={pdfUrl}
                                    apiKey={apiKey}
                                    row={row}
                                />
                            </div>
                        </Tabs.Panel>
                    );
                })}
            </Tabs>
        );
    },
);

const TopToolbarCustomActions = ({
    table,
    index,
    data,
    columnFilters,
}: {
    table: MRT_TableInstance<TableData>;
    index: string;
    data: InfiniteData<{ hits: { hits: TableData[] } }> | undefined;
    columnFilters: MRT_ColumnFiltersState;
}) => {
    const MAX_REPORTS = 100;
    const filteredRows = Object.keys(table.getFilteredRowModel().rowsById);
    const hasFilters = Boolean(columnFilters.length);
    let rows =
        hasFilters && filteredRows.length <= MAX_REPORTS ? filteredRows.slice(0, MAX_REPORTS) : [];
    rows = rows.map((str) => str.split("_")[0]);
    const navigate = useNavigate();
    const sources = useColumns(index);

    const mappedIndex = useMemo(() => {
        if (sources.data === null) return "";
        return sources?.data?.namespace;
    }, [sources]);
    // biome-ignore lint/correctness/useExhaustiveDependencies: sending the array as is causes constant change
    const newChatId = useMemo(() => {
        const chatEntity = ChatEntity._(mappedIndex);
        return chatEntity.id;
    }, [undefined, mappedIndex]);

    const useMessages = (chatId: string) =>
        useLiveQuery(() => {
            if (!chatId) return [];
            return db.messages.where("chatId").equals(chatId).sortBy("createdAt");
        }, [chatId]);

    const messages = useMessages(newChatId);
    const [opened, { close }] = useDisclosure(false);
    const { setSelectedTab } = useTab();
    const { instance } = useMsal();
    const account = useAccount();
    const apiKey = account?.idToken;

    const flatData = useMemo(
        () =>
            data?.pages.flatMap((page) =>
                page.hits.hits.map((doc) => ({
                    ...doc,
                    fields: doc._source.metadata,
                })),
            ) ?? [],
        [data],
    );

    useEffect(() => {
        if (mappedIndex !== "") {
            setSelectedTab(mappedIndex);
        }
    }, [mappedIndex, setSelectedTab]);

    return (
        <Drawer
            styles={{
                header: { display: "none" },
                body: {
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    padding: 0,
                    overflow: "hidden",
                },
                content: { height: "100vh", overflow: "hidden" },
            }}
            opened={opened}
            onClose={() => {
                navigate({ to: "/documents" });
                close();
            }}
            title={null}
            size="100%"
        >
            <div className="flex h-full overflow-hidden">
                {/* Chat Panel */}
                <div className="flex w-[40%] flex-col overflow-hidden border-r border-gray-200 bg-gray-50">
                    {/* Header */}
                    <div className="shrink-0 border-b border-gray-200 bg-white p-4 shadow-sm">
                        <Anchor
                            c="blue"
                            className="mb-2 inline-flex items-center text-sm font-medium hover:underline"
                            onClick={() => {
                                navigate({ to: "/documents" });
                                close();
                            }}
                        >
                            ← Back to Document Selection
                        </Anchor>
                        <Title order={4} className="text-gray-800">
                            Chat with {rows.length} {rows.length === 1 ? "Document" : "Documents"}
                        </Title>
                        <p className="mt-1 truncate text-sm text-gray-500" title={rows.join(", ")}>
                            {rows.join(", ")}
                        </p>
                    </div>

                    {/* Chat Area */}
                    <ScrollArea
                        className="flex-1"
                        styles={{
                            viewport: { alignContent: "end", height: "100%" },
                            root: { height: "100%" },
                        }}
                    >
                        <div className="p-4">
                            <ChatRouteChild
                                isChatWithDocument
                                isCompact
                                files={rows}
                                chatId={newChatId}
                                messages={messages}
                                customStyles={{
                                    width: "100%",
                                    borderRadius: "12px",
                                    border: "1px solid #e5e7eb",
                                    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
                                }}
                            />
                        </div>
                    </ScrollArea>
                </div>

                {/* PDF Panel */}
                <div className="flex w-[60%] flex-col overflow-hidden bg-white">
                    <PDFList
                        rows={rows}
                        // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
                        metadataDocumentsLive={flatData ?? ([] as any)}
                        instance={instance}
                        apiKey={apiKey}
                    />
                </div>
            </div>
        </Drawer>
    );
};

export default TopToolbarCustomActions;
